import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { AttachmentFile } from '../types/attachment';

export const PUBLIC_ATTACHMENT_BASE_URL = 'https://bus.bnfc.or.kr';

export const buildPublicAttachmentUrl = (attachmentId: string, storagePath?: string) => {
  const params = new URLSearchParams({ file: attachmentId });
  if (storagePath) {
    params.set('path', storagePath);
  }
  return `${PUBLIC_ATTACHMENT_BASE_URL}/?${params.toString()}`;
};

interface PublicAttachmentPageProps {
  fallback?: React.ReactNode;
}

export const PublicAttachmentPage: React.FC<PublicAttachmentPageProps> = ({ fallback }) => {
  const { attachmentId: routeAttachmentId } = useParams<{ attachmentId: string }>();
  const [searchParams] = useSearchParams();
  const attachmentId = routeAttachmentId || searchParams.get('file') || undefined;
  const storagePath = searchParams.get('path') || undefined;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const redirectToFile = async () => {
      if (!attachmentId) {
        if (!fallback) {
          setError('첨부파일 링크가 올바르지 않습니다.');
        }
        return;
      }

      try {
        const snapshot = await getDoc(doc(db, 'attachments', attachmentId));
        if (!snapshot.exists()) {
          setError('첨부파일을 찾을 수 없습니다.');
          return;
        }

        const attachment = snapshot.data() as AttachmentFile;
        if (!attachment.downloadUrl) {
          setError('첨부파일 URL이 준비되지 않았습니다.');
          return;
        }

        window.location.replace(attachment.downloadUrl);
      } catch (err) {
        console.error('Error opening public attachment:', err);

        if (storagePath) {
          try {
            const downloadUrl = await getDownloadURL(ref(storage, storagePath));
            window.location.replace(downloadUrl);
            return;
          } catch (storageErr) {
            console.error('Error opening public attachment from storage path:', storageErr);
          }
        }

        setError('첨부파일을 여는 중 오류가 발생했습니다.');
      }
    };

    redirectToFile();
  }, [attachmentId, fallback, storagePath]);

  if (!attachmentId && fallback) {
    return <>{fallback}</>;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
        p: 3,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              첨부파일을 여는 중입니다.
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};
